// The BRANCH ↔ dataset-module table — the single place that says which chunk
// belongs to which audience branch. Pure data, no imports, so both the browser
// loader (faqBranchData.js) and the translation loader
// (translations/loadFaqTranslations.js) can read it without pulling anything in.
//
// THE RULE IT ENCODES (Marin, 2026-08-10)
// ---------------------------------------
// "für andere rollen darfst du nicht anzeigen, der Agent und Agency darf sicher
// nicht wissen was alles die Clubs haben!" — a viewer sees ONLY the questions of
// their own tiers, and foreign content must not even reach the client. So the
// dataset is split into modules that are each downloaded only by viewers who own
// one of the module's branches.
//
// THE INVARIANT (guarded by faqBranchModules.test.js)
// --------------------------------------------------
// For every module, EVERY entry's `aud` set equals the module's branch set.
// That is what makes the split leak-free in both directions:
//   * a viewer who loads the module owns one of its branches, and every entry
//     in it carries that branch → nothing foreign is delivered;
//   * a viewer who owns a branch loads every module carrying it → nothing of
//     their own is withheld.
// This is why the Doppel-Ast rows are their OWN modules (agentClub,
// agentAgencyAdmin, staffAgent) instead of sitting inside the tier file they
// were authored in: a club member must get the shared radar/contract rows
// WITHOUT the agent-only ones around them. (clubAdminAgencyAdmin, the former
// 2c+3a module, was removed in the 2026-08 content cleanup when its single
// question was cut.)

export const FAQ_MODULE_BRANCHES = {
  basis: ["0"],
  agent: ["1a"],
  agentClub: ["1a", "1c"],
  // Function-anchors wave (2026-08-12): /documents is open to clubless
  // staff too, so the signing trio lives in its own 1a+1c+1s module.
  agentClubStaff: ["1a", "1c", "1s"],
  agentAgencyAdmin: ["1a", "3a"],
  agencyMember: ["2a"],
  agencyAdmin: ["3a"],
  staffFree: ["1s"],
  staffAgent: ["1s", "1a"],
  clubMember: ["1c"],
  clubAdmin: ["2c"],
  lawyer: ["lawyer"],
  // G1 (2026-08-12): own mini-branches for player and organizer accounts.
  // Nobody else owns these branches (utils/faqRoles.js), so the chunks are
  // delivered to exactly these two account types and no one else.
  player: ["player"],
  organizer: ["organizer"],
};

// Stable module order → stable question order for a given viewer (the sort in
// faqStructure.sortQuestions only orders by tab and group; within a group the
// module order decides).
export const FAQ_MODULE_ORDER = Object.keys(FAQ_MODULE_BRANCHES);

/**
 * The dataset modules a viewer with these branches may download.
 * @param {Set<string>|Array<string>} branches the viewer's own branches
 * @returns {string[]} module names, in FAQ_MODULE_ORDER
 */
export const modulesForBranches = (branches) => {
  const owned = branches instanceof Set ? branches : new Set(branches || []);
  return FAQ_MODULE_ORDER.filter((name) =>
    FAQ_MODULE_BRANCHES[name].some((b) => owned.has(b))
  );
};

export default FAQ_MODULE_BRANCHES;
